import {
  FrequentQuestionsSection,
  HowItWorksSection,
  NewsSection,
} from "../../organims";
import "./styles.scss";
import { HOME_BANNER } from "../../../assets";
import { useTranslation } from "react-i18next";
import CategoryGrid from "../../organims/CardDetail/categoryGrid";
import { useCallback, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import Loader from "../../moleculas/Loader";
import ErrorMessage from "../../moleculas/ErrorMessage";
import { STATISTICS_DETAIL } from "../../../constants/routes";
import CmsPublicApiClient, {
  CmsApiError,
} from "../../../services/api/cmsPublicApiClient";
import { mapOrganizationToVM } from "../../../services/mappers/cmsPublicMappers";
import { OrganizationNodesVM } from "../../../types/viewModels/cmsPublic";

const service = new CmsPublicApiClient();
const stripOrganizationWord = (value: string) =>
  value.replace(/\borganizaciones?\b/gi, "").replace(/\s{2,}/g, " ").trim();

const buildErrorMessage = (error: unknown) => {
  if (error instanceof CmsApiError) {
    if (error.status === 404) {
      return "No se encontraron organizaciones publicadas.";
    }

    if (error.status === 400 || error.status === 422) {
      return "Los parámetros solicitados para organizaciones no son válidos.";
    }

    return "No fue posible cargar organizaciones desde CMS.";
  }

  return "Ocurrió un error inesperado al cargar organizaciones.";
};

const Home = () => {
  const { t } = useTranslation("home");
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState<OrganizationNodesVM[]>([]);
  const [error, setError] = useState<string>("");

  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await service.getOrganizations(1, 20);
      const mapped = response.data.map(mapOrganizationToVM);
      setOrganizations(mapped);
    } catch (requestError) {
      setOrganizations([]);
      setError(buildErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  return (
    <div className="w-100 d-flex justify-content-md-start justify-content-lg-start flex-column">
      <div className="w-100">
        <div className="w-100 banner-img-container d-flex justify-content-center">
          <div className="dark-background"></div>
          <img src={HOME_BANNER} className="banner-img" alt="..." />
          <div className="banner-content w-75 h-100 flex-column d-flex align-items-start justify-content-center">
            <div className="banner-text mb-4">
              <h1 className="mb-4">{t("section")}</h1>
              <p>{t("sectionDescription")}</p>
            </div>
          </div>
        </div>
        <div className="w-100 banner-bar"></div>
        <div className="w-100 d-flex flex-column align-items-center background-color">
          <div className="w-75 feature-section-container">
            {loading ? <Loader /> : null}

            {!loading && error ? (
              <div className="d-flex flex-column align-items-center gap-3">
                <ErrorMessage message={error} />
                <Button onClick={fetchOrganizations} className="custom-primary-button">
                  Reintentar
                </Button>
              </div>
            ) : null}

            {!loading && !error && organizations.length === 0 ? (
              <ErrorMessage message="No hay organizaciones publicadas para mostrar." />
            ) : null}

            {!loading && !error && organizations.length > 0 ? (
              <>
                <div className="mb-4">
                  <CategoryGrid
                    items={organizations.map((organization) => ({
                        title: stripOrganizationWord(organization.name) || organization.name,
                        description: organization.subtitle || "",
                        iconSrc: organization.iconSrc,
                        iconAlt: organization.name,
                        href: STATISTICS_DETAIL.replace(
                          ":id",
                          encodeURIComponent(organization.slug)
                        ),
                    }))}
                  />
                </div>
              </>
            ) : null}
          </div>
        </div>
        <div className="w-100 d-flex flex-column align-items-center background-color">
          <div className="w-75 mt-3">
            <HowItWorksSection />
          </div>
        </div>
        <div className="w-100 d-flex flex-column align-items-center">
          <div className="w-75 mt-5">
            <NewsSection />
          </div>
        </div>
        <div className="w-100 d-flex flex-column align-items-center">
          <div className="col-lg-6 col-10 mt-5">
            <FrequentQuestionsSection questionNumber={3} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
